import React, { createContext, useContext, useEffect, useState } from 'react';

const THEME_STORAGE_KEY = '360dg-theme';

function getInitialTheme() {
  if (typeof window === 'undefined') return 'dark';
  try {
    return window.sessionStorage.getItem(THEME_STORAGE_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

const ThemeContext = createContext({ theme: 'dark', setTheme: () => {}, isDark: true });

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    const html = document.documentElement;
    html.classList.toggle('dark', theme === 'dark');
    html.dataset.theme = theme;
    try {
      window.sessionStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Theme still works without persisted storage.
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#08111F' : '#F4F6FB');
  }, [theme]);

  const setTheme = (next) => setThemeState(next === 'dark' ? 'dark' : 'light');
  const isDark = theme === 'dark';

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

const SunIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="tt-icon tt-icon--sun">
    <circle cx="12" cy="12" r="4.4" fill="currentColor" />
    <path
      d="M12 2.7v2.1M12 19.2v2.1M4.8 4.8l1.5 1.5M17.7 17.7l1.5 1.5M2.7 12h2.1M19.2 12h2.1M4.8 19.2l1.5-1.5M17.7 6.3l1.5-1.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const MoonIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="tt-icon tt-icon--moon">
    <path
      d="M19.5 14.7A7.8 7.8 0 0 1 9.3 4.5 8 8 0 1 0 19.5 14.7Z"
      fill="currentColor"
    />
  </svg>
);

export const ThemeToggle = ({ className = '' }) => {
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';
  const flip = () => setTheme(isDark ? 'light' : 'dark');

  return (
    <button
      type="button"
      className={`theme-toggle ${className}`.trim()}
      onClick={flip}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      <span className="tt-button-icon" aria-hidden="true">
        {isDark ? <MoonIcon /> : <SunIcon />}
      </span>
    </button>
  );
};
